import { normalizeName } from './allergy-check.js';

/* ------------------------------------------------------------------ */
/*  Extracted allergy review — classification + state logic (PRD.272). */
/*                                                                      */
/*  Pure and dependency-light (only allergy-check.normalizeName) so it  */
/*  stays unit-testable, mirroring allergy-check.ts. No React/PowerSync.*/
/*                                                                      */
/*  An extracted allergy is only an AI-detected mention — nothing here  */
/*  writes; it classifies mentions and computes review-state           */
/*  transitions. Confirmation + writes happen through the existing      */
/*  patient_allergies workflow behind explicit user action.            */
/* ------------------------------------------------------------------ */

export type AllergyClassification =
  | 'new'
  | 'already_recorded'
  | 'possible_duplicate'
  | 'previously_removed';

/** Persisted review-state lifecycle (visit_extraction_allergy_reviews.status). */
export type AllergyReviewStatus =
  | 'pending'
  | 'matched_existing'
  | 'possible_duplicate'
  | 'previously_removed'
  | 'needs_more_information'
  | 'confirmed_created'
  | 'dismissed';

export type AllergyResolutionAction =
  | 'created'
  | 'created_separately'
  | 'matched'
  | 'restored'
  | 'dismissed';

export interface AllergyRef {
  id: string;
  substance: string | null | undefined;
}

export interface AllergyClassificationResult {
  classification: AllergyClassification;
  /** Existing allergy this mention matched or resembles (active or removed). */
  matchedAllergyId?: string;
  /** Substance of the matched allergy (for "Possible duplicate: X" display). */
  matchedSubstance?: string;
  /** True when the match is a soft-deleted (previously removed) allergy. */
  matchedRemoved?: boolean;
}

const MIN_SIMILAR_LEN = 3;
const SIMILAR_LEN_RATIO = 0.6;

/**
 * Similarity beyond an exact normalized match: substring containment either
 * direction (e.g. "peanut" ↔ "peanuts", "penicillin" ↔ "penicillin g"),
 * guarded by (a) a minimum length and (b) a length ratio so a short token isn't
 * flagged as a duplicate of a much longer unrelated word ("ace" vs
 * "acetaminophen"). This is the approved possible-duplicate behavior for v1
 * (PRD.272 Open Q4); exact matches are handled separately and take priority.
 */
function isSimilar(a: string, b: string): boolean {
  if (!a || !b) return false;
  if (Math.min(a.length, b.length) < MIN_SIMILAR_LEN) return false;
  if (!(a.includes(b) || b.includes(a))) return false;
  return Math.min(a.length, b.length) / Math.max(a.length, b.length) >= SIMILAR_LEN_RATIO;
}

/**
 * Classify an extracted allergy mention against the patient's active and
 * soft-deleted allergies. Priority: exact-active → exact-removed →
 * similar-active → similar-removed → new.
 */
export function classifyExtractedAllergy(
  rawValue: string,
  active: readonly AllergyRef[],
  removed: readonly AllergyRef[],
): AllergyClassificationResult {
  const needle = normalizeName(rawValue);
  if (!needle) return { classification: 'new' };

  const activeExact = active.find((a) => normalizeName(a.substance) === needle);
  if (activeExact) {
    return {
      classification: 'already_recorded',
      matchedAllergyId: activeExact.id,
      matchedSubstance: activeExact.substance ?? undefined,
    };
  }

  const removedExact = removed.find((a) => normalizeName(a.substance) === needle);
  if (removedExact) {
    return {
      classification: 'previously_removed',
      matchedAllergyId: removedExact.id,
      matchedSubstance: removedExact.substance ?? undefined,
      matchedRemoved: true,
    };
  }

  const activeSimilar = active.find((a) => isSimilar(needle, normalizeName(a.substance)));
  if (activeSimilar) {
    return {
      classification: 'possible_duplicate',
      matchedAllergyId: activeSimilar.id,
      matchedSubstance: activeSimilar.substance ?? undefined,
    };
  }

  const removedSimilar = removed.find((a) => isSimilar(needle, normalizeName(a.substance)));
  if (removedSimilar) {
    return {
      classification: 'previously_removed',
      matchedAllergyId: removedSimilar.id,
      matchedSubstance: removedSimilar.substance ?? undefined,
      matchedRemoved: true,
    };
  }

  return { classification: 'new' };
}

/** Initial persisted status derived from a classification. */
export function initialStatusFor(c: AllergyClassification): AllergyReviewStatus {
  switch (c) {
    case 'new':
      return 'pending';
    case 'already_recorded':
      return 'matched_existing';
    case 'possible_duplicate':
      return 'possible_duplicate';
    case 'previously_removed':
      return 'previously_removed';
  }
}

/** Status a review row takes after a resolution action. */
export function statusForAction(action: AllergyResolutionAction): AllergyReviewStatus {
  switch (action) {
    case 'created':
    case 'created_separately':
      return 'confirmed_created';
    case 'matched':
    case 'restored':
      return 'matched_existing';
    case 'dismissed':
      return 'dismissed';
  }
}

/** New mentions may be preselected; everything else must not be (PRD Selection Rules). */
export function defaultSelected(c: AllergyClassification): boolean {
  return c === 'new';
}

/** Already-recorded mentions may never be selected for creation. */
export function isSelectable(c: AllergyClassification): boolean {
  return c !== 'already_recorded';
}

/**
 * Whether a persisted item still needs clinician attention (drives whether the
 * review prompt should auto-open). Resolved/dismissed items never re-prompt.
 */
export function requiresAttention(status: AllergyReviewStatus): boolean {
  return (
    status === 'pending' ||
    status === 'possible_duplicate' ||
    status === 'previously_removed' ||
    status === 'needs_more_information'
  );
}

/**
 * Deterministic review-row id for a mention. Because the id encodes
 * (extraction, normalized value), a re-send from any tab/device uses the SAME
 * primary key, so the server upserts on (id) instead of creating a duplicate
 * that would violate the unique index and poison the PowerSync upload queue.
 */
export function allergyReviewId(extractionId: string, normalizedValue: string): string {
  return `vear_${extractionId}_${normalizeName(normalizedValue).replace(/[^a-z0-9]+/g, '-')}`;
}

/**
 * Count mentions that still need clinician attention (PRD.273 "N allergies to
 * review"). An item with a persisted status uses `requiresAttention`; an
 * unpersisted item needs attention unless it's already recorded.
 */
export function unresolvedAllergyCount(
  items: readonly { classification: AllergyClassification; status?: string | null }[],
): number {
  return items.filter((it) =>
    it.status
      ? requiresAttention(it.status as AllergyReviewStatus)
      : it.classification !== 'already_recorded',
  ).length;
}

/** Trim + case-insensitively de-duplicate raw allergy mentions (normalize-based). */
export function dedupeAllergyMentions(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const trimmed = v.trim();
    if (!trimmed) continue;
    const key = normalizeName(trimmed);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

/**
 * Final duplicate re-check helper — returns the id of an active allergy that
 * exactly matches (normalized), or null. Callers MUST run this against a fresh
 * active-allergy read immediately before writing (PRD Write Behavior §1).
 */
export function findActiveExactMatch(
  rawValue: string,
  active: readonly AllergyRef[],
): string | null {
  const needle = normalizeName(rawValue);
  if (!needle) return null;
  const hit = active.find((a) => normalizeName(a.substance) === needle);
  return hit ? hit.id : null;
}

/** Provenance payload persisted on the review row for a resolved item. */
export interface AllergyReviewProvenance {
  extractionId: string;
  visitId: string;
  sourceRecordingId: string | null;
  rawValue: string;
  normalizedValue: string;
  action: AllergyResolutionAction;
  resolvedAllergyId: string | null;
  confirmedBy: string | null;
  confirmedAt: string;
}

/** Build the provenance record linking a review resolution to its allergy. */
export function buildAllergyReviewProvenance(input: {
  extractionId: string;
  visitId: string;
  sourceRecordingId?: string | null;
  rawValue: string;
  action: AllergyResolutionAction;
  resolvedAllergyId?: string | null;
  confirmedBy?: string | null;
  now: string;
}): AllergyReviewProvenance {
  return {
    extractionId: input.extractionId,
    visitId: input.visitId,
    sourceRecordingId: input.sourceRecordingId ?? null,
    rawValue: input.rawValue,
    normalizedValue: normalizeName(input.rawValue),
    action: input.action,
    resolvedAllergyId: input.resolvedAllergyId ?? null,
    confirmedBy: input.confirmedBy ?? null,
    confirmedAt: input.now,
  };
}
